import { Injectable, NgZone } from '@angular/core';
import { ValidationPatterns } from '../constants/validation-patterns.constants';

/**
 * Runtime DOM Sanitizer Service
 * Uses MutationObserver to detect and neutralize XSS attacks in real-time
 * This provides a last line of defense against DOM-based XSS, including
 * attacks via dev tools manipulation or stored XSS payloads
 */
@Injectable({
  providedIn: 'root'
})
export class DomSanitizerService {
  private observer: MutationObserver | null = null;
  private isInitialized = false;

  constructor(private ngZone: NgZone) {}

  /**
   * Initialize the DOM sanitizer - call this from AppComponent ngOnInit
   */
  initialize(): void {
    if (this.isInitialized || typeof MutationObserver === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      // Initial scan of existing DOM
      this.sanitizeElement(document.documentElement);

      // Set up observer for future changes
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Handle added nodes
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLElement) {
                this.sanitizeElement(node);
              }
            });
          }
          // Handle attribute changes
          else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
            this.sanitizeAttribute(mutation.target, mutation.attributeName || '');
          }
        });
      });

      // Observe the entire document
      this.observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeOldValue: true
      });

      this.isInitialized = true;
      console.log('DOM Sanitizer initialized - XSS protection active');
    });
  }

  /**
   * Sanitize an element and all its children
   */
  private sanitizeElement(element: HTMLElement): void {
    // Check this element's attributes
    this.sanitizeAllAttributes(element);

    // Check all child elements
    const children = element.querySelectorAll('*');
    children.forEach((child) => {
      if (child instanceof HTMLElement) {
        this.sanitizeAllAttributes(child);
      }
    });
  }

  /**
   * Sanitize all attributes of an element
   */
  private sanitizeAllAttributes(element: HTMLElement): void {
    const attributesToRemove: string[] = [];

    // Collect dangerous attributes
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (this.isDangerousAttribute(attr.name, attr.value)) {
        attributesToRemove.push(attr.name);
      }
    }

    // Remove them (outside the iteration to avoid issues)
    attributesToRemove.forEach((attrName) => {
      console.warn(`XSS Protection: Removed dangerous attribute "${attrName}" from <${element.tagName.toLowerCase()}>`);
      element.removeAttribute(attrName);
    });

    // Check and sanitize href/src attributes
    this.sanitizeUrlAttributes(element);
  }

  /**
   * Sanitize a specific attribute
   */
  private sanitizeAttribute(element: HTMLElement, attributeName: string): void {
    if (!attributeName) return;

    const value = element.getAttribute(attributeName) || '';

    if (this.isDangerousAttribute(attributeName, value)) {
      console.warn(`XSS Protection: Blocked dangerous attribute "${attributeName}" on <${element.tagName.toLowerCase()}>`);
      element.removeAttribute(attributeName);
    }

    // Check URL attributes
    if (['href', 'src', 'action', 'formaction', 'data', 'poster', 'background'].includes(attributeName.toLowerCase())) {
      this.sanitizeUrlAttribute(element, attributeName);
    }
  }

  /**
   * Check if an attribute is dangerous
   */
  private isDangerousAttribute(name: string, value: string): boolean {
    const nameLower = name.toLowerCase();

    // Check against known dangerous attributes
    if (ValidationPatterns.DANGEROUS_ATTRIBUTES.includes(nameLower)) {
      return true;
    }

    // Check against patterns
    if (ValidationPatterns.DANGEROUS_ATTRIBUTE_PATTERNS.some(pattern => pattern.test(nameLower))) {
      return true;
    }

    // Check for javascript: or other dangerous protocols in value
    if (value) {
      const valueLower = value.toLowerCase().trim();
      if (valueLower.startsWith('javascript:') ||
          valueLower.startsWith('vbscript:') ||
          valueLower.startsWith('data:text/html')) {
        return true;
      }
    }

    return false;
  }

  /**
   * Sanitize URL-based attributes
   */
  private sanitizeUrlAttributes(element: HTMLElement): void {
    const urlAttributes = ['href', 'src', 'action', 'formaction', 'data', 'poster', 'background', 'xlink:href'];
    
    urlAttributes.forEach(attr => {
      if (element.hasAttribute(attr)) {
        this.sanitizeUrlAttribute(element, attr);
      }
    });
  }

  /**
   * Sanitize a single URL attribute
   */
  private sanitizeUrlAttribute(element: HTMLElement, attributeName: string): void {
    const value = element.getAttribute(attributeName);
    if (!value) return;

    const valueLower = value.toLowerCase().trim();

    // Block dangerous protocols
    if (valueLower.startsWith('javascript:') ||
        valueLower.startsWith('vbscript:') ||
        valueLower.startsWith('data:text/html') ||
        valueLower.startsWith('data:application')) {
      console.warn(`XSS Protection: Removed dangerous URL in "${attributeName}" attribute`);
      element.removeAttribute(attributeName);
    }
  }

  /**
   * Stop the observer (call on app destroy)
   */
  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.isInitialized = false;
  }

  /**
   * Manually trigger a scan of the entire document
   */
  scanDocument(): void {
    this.sanitizeElement(document.documentElement);
  }
}
